/**
 * Course Visuals
 * Derives a rich, deterministic "subject identity" for a course from its title:
 * a gradient cover, an accent color, and a subject glyph. This gives every course
 * a distinct, magazine-style cover (Brilliant-inspired) instead of a generic icon.
 */

export type GlyphKey =
  | 'biology'
  | 'chemistry'
  | 'physics'
  | 'math'
  | 'cs'
  | 'history'
  | 'literature'
  | 'art'
  | 'business'
  | 'earth'
  | 'psychology'
  | 'music'
  | 'health'
  | 'engineering'
  | 'academic';

export interface CourseVisual {
  /** CSS linear-gradient string for the cover background */
  gradient: string;
  /** Solid accent color (hex) used for rings, chips, focus */
  accent: string;
  /** Soft accent tint (rgba) for subtle backgrounds */
  accentSoft: string;
  /** Which subject glyph to render */
  glyph: GlyphKey;
  /** Human-readable subject label */
  label: string;
}

interface SubjectDef {
  keywords: string[];
  glyph: GlyphKey;
  label: string;
  gradient: string;
  accent: string;
  accentSoft: string;
}

/**
 * Rich subject palettes. Gradients are three-stop and diagonal for depth.
 * Keep colors saturated but not neon so they read as "premium" not "toy".
 */
const SUBJECTS: SubjectDef[] = [
  {
    keywords: ['biolog', 'life scien', 'genetic', 'cell', 'ecolog', 'zoolog', 'botan', 'anatomy', 'physiolog', 'microbiol'],
    glyph: 'biology',
    label: 'Biology',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #10b981 55%, #34d399 100%)',
    accent: '#10b981',
    accentSoft: 'rgba(16, 185, 129, 0.14)',
  },
  {
    keywords: ['chem', 'organic', 'inorganic', 'biochem', 'molecul'],
    glyph: 'chemistry',
    label: 'Chemistry',
    gradient: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 55%, #c084fc 100%)',
    accent: '#8b5cf6',
    accentSoft: 'rgba(139, 92, 246, 0.14)',
  },
  {
    keywords: ['physic', 'mechanic', 'quantum', 'thermodynam', 'relativ', 'optic', 'electromagnet'],
    glyph: 'physics',
    label: 'Physics',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 55%, #60a5fa 100%)',
    accent: '#3b82f6',
    accentSoft: 'rgba(59, 130, 246, 0.14)',
  },
  {
    keywords: ['math', 'algebra', 'calculus', 'geometry', 'statist', 'trigonom', 'linear', 'discrete', 'number theory'],
    glyph: 'math',
    label: 'Mathematics',
    gradient: 'linear-gradient(135deg, #4338ca 0%, #6366f1 55%, #818cf8 100%)',
    accent: '#6366f1',
    accentSoft: 'rgba(99, 102, 241, 0.14)',
  },
  {
    keywords: ['comput', 'programm', 'software', 'coding', 'data struct', 'algorithm', 'machine learn', 'artificial intel', ' ai', 'cyber', 'web dev', 'database', 'network'],
    glyph: 'cs',
    label: 'Computer Science',
    gradient: 'linear-gradient(135deg, #0e7490 0%, #06b6d4 55%, #22d3ee 100%)',
    accent: '#06b6d4',
    accentSoft: 'rgba(6, 182, 212, 0.14)',
  },
  {
    keywords: ['histor', 'civiliz', 'ancient', 'medieval', 'modern era', 'world war', 'political scien', 'government'],
    glyph: 'history',
    label: 'History',
    gradient: 'linear-gradient(135deg, #b45309 0%, #d97706 55%, #f59e0b 100%)',
    accent: '#d97706',
    accentSoft: 'rgba(217, 119, 6, 0.14)',
  },
  {
    keywords: ['literat', 'english', 'writing', 'poetry', 'language', 'linguist', 'grammar', 'compositi', 'rhetoric', 'reading'],
    glyph: 'literature',
    label: 'Literature',
    gradient: 'linear-gradient(135deg, #be123c 0%, #e11d48 55%, #fb7185 100%)',
    accent: '#e11d48',
    accentSoft: 'rgba(225, 29, 72, 0.14)',
  },
  {
    keywords: ['art', 'design', 'paint', 'draw', 'sculpt', 'photograph', 'visual', 'graphic', 'architect'],
    glyph: 'art',
    label: 'Art & Design',
    gradient: 'linear-gradient(135deg, #a21caf 0%, #d946ef 55%, #f0abfc 100%)',
    accent: '#d946ef',
    accentSoft: 'rgba(217, 70, 239, 0.14)',
  },
  {
    keywords: ['business', 'econom', 'finance', 'account', 'market', 'management', 'entrepren', 'commerce', 'trade'],
    glyph: 'business',
    label: 'Business',
    gradient: 'linear-gradient(135deg, #047857 0%, #059669 55%, #10b981 100%)',
    accent: '#059669',
    accentSoft: 'rgba(5, 150, 105, 0.14)',
  },
  {
    keywords: ['geograph', 'geolog', 'earth', 'climate', 'environment', 'astronom', 'space', 'planet', 'ocean'],
    glyph: 'earth',
    label: 'Earth & Space',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #0891b2 55%, #38bdf8 100%)',
    accent: '#0891b2',
    accentSoft: 'rgba(8, 145, 178, 0.14)',
  },
  {
    keywords: ['psycholog', 'social', 'sociolog', 'behavio', 'cognit', 'anthropolog', 'philosoph'],
    glyph: 'psychology',
    label: 'Psychology',
    gradient: 'linear-gradient(135deg, #c2410c 0%, #ea580c 55%, #fb923c 100%)',
    accent: '#ea580c',
    accentSoft: 'rgba(234, 88, 12, 0.14)',
  },
  {
    keywords: ['music', 'orchestr', 'compos', 'instrument', 'vocal', 'theory of music'],
    glyph: 'music',
    label: 'Music',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 55%, #e879f9 100%)',
    accent: '#a855f7',
    accentSoft: 'rgba(168, 85, 247, 0.14)',
  },
  {
    keywords: ['health', 'medic', 'nursing', 'pharma', 'clinical', 'nutrition', 'disease'],
    glyph: 'health',
    label: 'Health',
    gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 55%, #5eead4 100%)',
    accent: '#14b8a6',
    accentSoft: 'rgba(20, 184, 166, 0.14)',
  },
  {
    keywords: ['engineer', 'mechanic', 'electric', 'civil', 'robot', 'circuit', 'manufactur'],
    glyph: 'engineering',
    label: 'Engineering',
    gradient: 'linear-gradient(135deg, #334155 0%, #475569 55%, #64748b 100%)',
    accent: '#64748b',
    accentSoft: 'rgba(100, 116, 139, 0.14)',
  },
];

/** Fallback palettes used when no subject keyword matches — chosen by title hash. */
const FALLBACK_PALETTES: Omit<SubjectDef, 'keywords' | 'glyph' | 'label'>[] = [
  { gradient: 'linear-gradient(135deg, #a8442f 0%, #c95d48 55%, #e2725b 100%)', accent: '#c95d48', accentSoft: 'rgba(201, 93, 72, 0.14)' },
  { gradient: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 55%, #60a5fa 100%)', accent: '#3b82f6', accentSoft: 'rgba(59, 130, 246, 0.14)' },
  { gradient: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 55%, #c084fc 100%)', accent: '#8b5cf6', accentSoft: 'rgba(139, 92, 246, 0.14)' },
  { gradient: 'linear-gradient(135deg, #0f766e 0%, #10b981 55%, #34d399 100%)', accent: '#10b981', accentSoft: 'rgba(16, 185, 129, 0.14)' },
  { gradient: 'linear-gradient(135deg, #b45309 0%, #d97706 55%, #f59e0b 100%)', accent: '#d97706', accentSoft: 'rgba(217, 119, 6, 0.14)' },
];

/** Stable string hash → non-negative int (djb2). */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Resolve the visual identity for a course from its title.
 * Deterministic: the same title always yields the same cover.
 */
export function getCourseVisual(title: string): CourseVisual {
  const haystack = ` ${title.toLowerCase()} `;

  for (const subject of SUBJECTS) {
    if (subject.keywords.some((kw) => haystack.includes(kw))) {
      return {
        gradient: subject.gradient,
        accent: subject.accent,
        accentSoft: subject.accentSoft,
        glyph: subject.glyph,
        label: subject.label,
      };
    }
  }

  const palette = FALLBACK_PALETTES[hashString(title) % FALLBACK_PALETTES.length];
  return {
    gradient: palette.gradient,
    accent: palette.accent,
    accentSoft: palette.accentSoft,
    glyph: 'academic',
    label: 'General',
  };
}

/** Up-to-2-character monogram from a course title, e.g. "Intro to Biology" → "IB". */
export function getCourseInitials(title: string): string {
  const words = title.trim().split(/\s+/).filter((w) => /[a-z0-9]/i.test(w[0] || ''));
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
